import FlexSimulatorPage from 'pages/FlexSimulatorPage/FlexSimulatorPage';
import { useEffect, useState } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { AlertProvider } from './components/layout/Alert/AlertContext';
import LoadingOverlay from './components/layout/LoadingOverlay/LoadingOverlay';
import { LayoutProvider } from './contexts/LayoutContext';
import AuthErrorPage from './pages/AuthError/AuthErrorPage';
import SelecaoLojaPage from './pages/SelecaoLoja/SelecaoLojaPage';
import SimulatorPage from './pages/SimulatorPage/SimulatorPage';
import { fetchEmpreendimentos } from './services/api';
import { authManager } from './services/authManager';
import './styles/global.css';

const decodeJwtPayload = jwt => {
  try {
    const base64 = jwt.split('.')[1].replaceAll('-', '+').replaceAll('_', '/');

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.codePointAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const getTokensFromUrl = () => {
  const urlParams = new URLSearchParams(globalThis.location.search);
  let tokens = urlParams.getAll('token');
  if (tokens.length === 0 && globalThis.location.hash) {
    const hashParams = new URLSearchParams(globalThis.location.hash.slice(1));
    tokens = hashParams.getAll('token');
  }
  return tokens;
};

function App() {
  const [authError, setAuthError] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [lojaSelecionada, setLojaSelecionada] = useState(false);
  const [nomeLojaAtiva, setNomeLojaAtiva] = useState('');
  // Inicializado sincronamente com base na URL para evitar que initializeApp rode antes da seleção de loja
  const [aguardandoSelecaoLoja, setAguardandoSelecaoLoja] = useState(
    () => getTokensFromUrl().length > 1
  );
  const [loading, setLoading] = useState(() => getTokensFromUrl().length <= 1);

  authManager.setAuthErrorCallback = setAuthError;

  useEffect(() => {
    const tokensJwt = getTokensFromUrl();

    if (tokensJwt.length > 1) {
      const payloads = tokensJwt
        .map(jwt => {
          const payload = decodeJwtPayload(jwt);
          return payload ? { jwt, ...payload } : null;
        })
        .filter(Boolean);
      if (payloads.length === 0) {
        // todos os tokens eram inválidos — cair no fluxo de erro de auth
        authManager.setAuthError('Tokens inválidos na URL');
        setAguardandoSelecaoLoja(false);
        return;
      }
      if (payloads.length === 1) {
        // apenas um sobreviveu — tratar como token único
        globalThis._iframeAuthToken = payloads[0].jwt;
        if (typeof globalThis._clearAuthTokenCache === 'function') {
          globalThis._clearAuthTokenCache();
        }
        setAguardandoSelecaoLoja(false);
        setLojaSelecionada(true);
        return;
      }
      setLojas(payloads);
    } else if (tokensJwt.length === 1) {
      // Token único: ativa diretamente sem exibir tela de seleção
      globalThis._iframeAuthToken = tokensJwt[0];
      if (typeof globalThis._clearAuthTokenCache === 'function') {
        globalThis._clearAuthTokenCache();
      }
      setLojaSelecionada(true);
    }
  }, []);

  useEffect(() => {
    // Não inicializa enquanto aguarda o usuário selecionar a loja
    if (aguardandoSelecaoLoja) return;

    const initializeApp = async () => {
      try {
        await Promise.allSettled([fetchEmpreendimentos(true), fetchEmpreendimentos(false)]);

        setLoading(false);
      } catch (error) {
        console.error('Error during app initialization:', error);
        setLoading(false);
      }
    };

    initializeApp();
  }, [aguardandoSelecaoLoja]);

  useEffect(() => {
    const handler = function (event) {
      if (event.data?.type === 'updateToken') {
        const novoToken = event.data.token;
        globalThis._iframeAuthToken = novoToken;
        if (typeof globalThis._clearAuthTokenCache === 'function') {
          globalThis._clearAuthTokenCache();
        }
        console.log('[postMessage] Token de autenticação atualizado');
      }
    };
    globalThis.addEventListener('message', handler);
    return () => globalThis.removeEventListener('message', handler);
  }, []);

  const handleLojaSelect = jwt => {
    globalThis._iframeAuthToken = jwt;
    if (typeof globalThis._clearAuthTokenCache === 'function') {
      globalThis._clearAuthTokenCache();
    }
    const loja = lojas.find(l => l.jwt === jwt);
    setNomeLojaAtiva(loja?.nome ?? '');
    setAguardandoSelecaoLoja(false); // dispara o initializeApp via useEffect
    setLoading(true);
    setLojaSelecionada(true);
  };

  const handleTrocarLoja = () => {
    globalThis._iframeAuthToken = null;
    if (typeof globalThis._clearAuthTokenCache === 'function') {
      globalThis._clearAuthTokenCache();
    }
    setNomeLojaAtiva('');
    setLojaSelecionada(false);
    setAguardandoSelecaoLoja(true);
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  if (lojas.length > 1 && !lojaSelecionada) {
    return <SelecaoLojaPage lojas={lojas} onSelect={handleLojaSelect} />;
  }

  return (
    <LayoutProvider
      onTrocarLoja={handleTrocarLoja}
      temMultiplasLojas={lojas.length > 1}
      nomeLojaAtiva={nomeLojaAtiva}
    >
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              authError ? (
                <AuthErrorPage
                  errorMessage={authError}
                  onTryAgain={() => {
                    setLoading(true);
                    setAuthError(null);
                    Promise.allSettled([
                      fetchEmpreendimentos(true), // MRV
                      fetchEmpreendimentos(false), // SENSIA
                    ])
                      .then(() => setLoading(false))
                      .catch(() => setLoading(false));
                  }}
                />
              ) : (
                <SimulatorPage />
              )
            }
          />
          <Route
            path="/simulador-flex"
            element={
              <AlertProvider>
                {authError ? (
                  <AuthErrorPage
                    errorMessage={authError}
                    onTryAgain={() => {
                      setLoading(true);
                      setAuthError(null);
                      Promise.allSettled([
                        fetchEmpreendimentos(true), // MRV
                        fetchEmpreendimentos(false), // SENSIA
                      ])
                        .then(() => setLoading(false))
                        .catch(() => setLoading(false));
                    }}
                  />
                ) : (
                  <FlexSimulatorPage />
                )}
              </AlertProvider>
            }
          />
        </Routes>
      </Router>
    </LayoutProvider>
  );
}

export default App;
